<div class="split">
  <section class="panel list-panel" aria-labelledby="requests-heading">
    <div class="section-heading">
      <div>
        <p class="eyebrow">FROM REGISTRATION CERTIFICATES</p>
        <h2 id="requests-heading">Requested data</h2>
      </div>
      @if (check(); as check) {
        <span
          class="quiet-status"
          [class.bad-status]="check.status === 'inconsistent'"
          [class.muted]="check.status === 'unavailable'"
          [title]="
            check.status === 'unavailable'
              ? 'Register not checked: ' + check.reason
              : 'Compared with /intended-uses at sequence ' + check.asOfSequence
          "
        >
          <mat-icon aria-hidden="true">{{
            check.status === "consistent"
              ? "verified"
              : check.status === "inconsistent"
                ? "gpp_bad"
                : "help_outline"
          }}</mat-icon>
          {{
            check.status === "consistent"
              ? "matches /intended-uses"
              : check.status === "inconsistent"
                ? "differs from /intended-uses"
                : "/intended-uses not checked"
          }}
        </span>
      }
    </div>

    <div class="toolbar">
      <div class="chips" role="group" aria-label="Intended uses">
        @for (option of scopes; track option.value) {
          <button
            type="button"
            class="chip"
            [class.active]="scope() === option.value"
            [attr.aria-pressed]="scope() === option.value"
            (click)="setScope(option.value)"
          >
            {{ option.label }}
            <span class="chip-count">{{ counts()[option.value] }}</span>
          </button>
        }
      </div>
      <label class="search">
        <mat-icon aria-hidden="true">search</mat-icon>
        <input
          type="search"
          placeholder="Search vct, doctype or claim"
          aria-label="Search attestation types and claims"
          [value]="query()"
          (input)="onQuery($event)"
        />
      </label>
    </div>

    @if (!visible().length) {
      <div class="empty-state">
        <mat-icon>manage_search</mat-icon>
        <h3>No attestation type matches</h3>
        <p>Search matches attestation types and claim paths.</p>
      </div>
    } @else {
      <ul class="rows" role="list">
        @for (entry of visible(); track entry.summary.type) {
          @let summary = entry.summary;
          <li>
            <button
              type="button"
              class="row"
              [class.selected]="selected()?.type === summary.type"
              [attr.aria-current]="selected()?.type === summary.type"
              (click)="select(summary)"
            >
              <span class="row-copy"
                ><strong class="mono type-name" [class.muted]="!summary.type">{{
                  typeName(summary.type)
                }}</strong
                ><small>
                  @for (format of summary.formats; track format) {
                    <span class="format">{{ formatLabel(format) }}</span>
                  }
                  {{ summary.uses.length }} intended
                  {{ summary.uses.length === 1 ? "use" : "uses" }}
                  @if (entry.matchedClaims.length) {
                    · claim
                    <span class="mono">{{ entry.matchedClaims[0].path }}</span>
                    @if (entry.matchedClaims.length > 1) {
                      +{{ entry.matchedClaims.length - 1 }}
                    }
                  }
                </small></span
              >
              <span class="row-meta">
                <strong class="wrp-count">{{ summary.wrpIds.size }}</strong>
                <small>{{ summary.wrpIds.size === 1 ? "WRP" : "WRPs" }}</small>
              </span>
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </li>
        }
      </ul>
    }
  </section>

  <aside class="panel detail-panel" aria-live="polite">
    @if (selected(); as summary) {
      <p class="eyebrow">ATTESTATION TYPE</p>
      <h2 class="mono type-title">{{ typeName(summary.type) }}</h2>
      <dl class="facts">
        <dt>Format</dt>
        <dd>
          @for (format of summary.formats; track format) {
            <span class="format">{{ formatLabel(format) }}</span>
          }
        </dd>
        <dt>Requested by</dt>
        <dd>
          {{ summary.wrpIds.size }}
          {{ summary.wrpIds.size === 1 ? "WRP" : "WRPs" }} in
          {{ summary.uses.length }} intended
          {{ summary.uses.length === 1 ? "use" : "uses" }}
        </dd>
        @if (summary.type) {
          <dt>Register</dt>
          <dd>
            <a [href]="apiLink(summary)" target="_blank" rel="noopener"
              >Search /intended-uses at #{{ context().asOfSequence }}</a
            >
          </dd>
        }
      </dl>

      <h3>Claims</h3>
      <div class="chips claim-chips" role="group" aria-label="Filter by claim">
        @for (entry of shownClaims(); track entry.path) {
          <button
            type="button"
            class="chip"
            [class.active]="claim() === entry.path"
            [attr.aria-pressed]="claim() === entry.path"
            (click)="toggleClaim(entry.path)"
          >
            <span class="mono">{{ entry.path }}</span>
            <span class="chip-count">{{ entry.wrpIds.size }}</span>
          </button>
        }
        @if (summary.undeclared.length) {
          <button
            type="button"
            class="chip"
            [class.active]="claim() === undeclared"
            [attr.aria-pressed]="claim() === undeclared"
            (click)="toggleClaim(undeclared)"
            title="The registration does not list individual attributes"
          >
            No claims declared
            <span class="chip-count">{{ summary.undeclared.length }}</span>
          </button>
        }
        @if (summary.claims.length > claimPreview) {
          <button
            type="button"
            class="link-button inline more-claims"
            (click)="allClaims.set(!allClaims())"
          >
            {{
              allClaims()
                ? "Fewer claims"
                : "All " + summary.claims.length + " claims"
            }}
          </button>
        }
      </div>

      <h3>
        Wallet-relying parties
        @if (claim()) {
          <span class="muted">· {{ groups().length }} with this filter</span>
        }
      </h3>
      <ul class="requester-list" role="list">
        @for (group of groups(); track group.wrpId) {
          <li>
            <a class="requester-name" [href]="wrpLink(group.wrpId)">{{
              group.name
            }}</a>
            @for (use of group.uses; track use.id) {
              @let paths = claimPaths(use);
              <div class="use">
                <p>
                  {{ purpose(use) }}
                  @if (!use.current) {
                    <span class="badge muted-badge">historic</span>
                  }
                </p>
                @if (paths) {
                  <p class="mono claims-line">
                    @for (path of paths; track path) {
                      <span [class.hit]="path === claim()">{{ path }}</span
                      >{{ $last ? "" : ", " }}
                    }
                  </p>
                } @else {
                  <p class="muted small">No claims declared</p>
                }
                <div class="use-links">
                  <button
                    type="button"
                    class="link-button inline"
                    (click)="inspect(use)"
                  >
                    Certificate #{{ use.latest.issued.sequence_number }}
                  </button>
                  <a
                    [href]="intendedUseLink(use)"
                    target="_blank"
                    rel="noopener"
                    class="mono"
                    >{{ use.id }}</a
                  >
                  @if (use.certificates.length > 1) {
                    <span class="muted"
                      >{{ use.certificates.length }} certificates</span
                    >
                  }
                </div>
              </div>
            }
          </li>
        }
      </ul>
    } @else {
      <div class="empty-state compact">
        <mat-icon>manage_search</mat-icon>
        <p>Select an attestation type.</p>
      </div>
    }
  </aside>
</div>
